import { XStack } from '@lyra/core/components'
import BodyText from '@lyra/core/components/BodyText'
import { Check, Plus } from '@lyra/core/components/Icon'
import { getCellPaddingProps, TableCellProps } from '@lyra/core/components/Table'
import useHover from '@lyra/core/hooks/useHover'
import usePress from '@lyra/core/hooks/usePress'
import formatUSD from '@lyra/core/utils/formatUSD'
import { getTextColorVar } from '@lyra/core/utils/text'
import { getBestAskPrice, getBestBidPrice } from '@lyra/web/utils/instruments'
import React, { useEffect, useRef, useState } from 'react'

import { countDecimals } from '../../../utils/number'
import { TradeOptionTableTickerData } from '.'

type Props = {
  isCall: boolean
  isBid: boolean
  isLarge: boolean
  tickSize: number
  isSelected: boolean
  onPress: () => void
  warningMessage?: string
} & TableCellProps<TradeOptionTableTickerData>

const FLASH_DURATION = 500

export default function TablePriceCell({
  isCall,
  isBid,
  isLarge,
  tickSize,
  isSelected,
  onPress,
  ...context
}: Props) {
  const { call, put } = context.row.original
  const ticker = isCall ? call : put
  const price = ticker ? (isBid ? getBestBidPrice(ticker) : getBestAskPrice(ticker)) : undefined

  const lastPrice = useRef<number>()

  const ref = useRef<HTMLElement>(null)

  const [flash, setFlash] = useState(false)

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined = undefined
    if (price && price !== lastPrice.current) {
      setFlash(true)
      timeout = setTimeout(() => setFlash(false), FLASH_DURATION)
    } else {
      setFlash(false)
    }
    lastPrice.current = price
    return () => clearTimeout(timeout)
  }, [price])

  const paddingProps = getCellPaddingProps(context, isLarge ? 'lg' : 'md')

  const { isHovering, handleHoverIn, handleHoverOut } = useHover()
  const { isPressing, handlePressIn, handlePressOut } = usePress()

  const bgColor = isSelected
    ? isBid
      ? '$redHairline'
      : '$greenHairline'
    : isPressing
    ? isBid
      ? '$redPressBg'
      : '$greenPressBg'
    : flash || isHovering
    ? isBid
      ? '$redBg'
      : '$greenBg'
    : undefined

  const textColor = isHovering || isSelected || price ? (isBid ? 'red' : 'green') : 'secondary'

  const priceText =
    price !== undefined
      ? formatUSD(price, { dps: countDecimals(tickSize) })
      : isHovering || isSelected
      ? formatUSD(0, { dps: countDecimals(tickSize) })
      : '-'

  return (
    <XStack
      group
      tag="button"
      ref={ref}
      // HACK: conditionally padding table cells inside Table results in mismatched column widths between rows and header
      // As a workaround, we offset padding using cell padding props and negative margin to expand to full size of parent cell
      // TODO: @earthtojake find a cleaner solution in the future
      marginLeft={-paddingProps['paddingLeft']}
      width={`calc(100% + ${paddingProps['paddingLeft'] + paddingProps['paddingRight']}px)`}
      paddingLeft={paddingProps['paddingLeft']}
      paddingRight={paddingProps['paddingRight']}
      height={`calc(100% + ${paddingProps['paddingVertical'] * 2}px)`}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      cursor="pointer"
      backgroundColor={bgColor}
      alignItems="center"
      onPress={(e) => {
        onPress()
        if (!price) {
          return
        }
        if (!isSelected) {
          // stop propagation when toggling bid/ask
          e.stopPropagation()
        }
      }}
    >
      <BodyText marginRight="$1" minWidth={55} color={textColor}>
        {priceText}
      </BodyText>
      {isSelected ? (
        <Check color={getTextColorVar(textColor)} size={12} strokeWidth={2.5} />
      ) : price || isHovering ? (
        <XStack
          borderColor={getTextColorVar(textColor)}
          borderWidth={1}
          width={13}
          height={13}
          borderRadius="$1"
          alignItems="center"
          justifyContent="center"
        >
          <Plus color={getTextColorVar(textColor)} size={9} strokeWidth={3} />
        </XStack>
      ) : null}
    </XStack>
  )
}
